import { useMutation } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";

export interface BackfillInput {
	dryRun: boolean;
	/** `YYYY-MM-DD`, inclusive. */
	from?: string;
	/** `YYYY-MM-DD`, exclusive. */
	to?: string;
}

/** Every page added up. */
export interface BackfillTotals {
	alreadyVerified: number;
	candidates: number;
	dryRun: boolean;
	enqueued: number;
	failed: number;
	mode: string;
	scannedEvents: number;
	skippedEdited: number;
	tooRecent: number;
}

/** Wire shape of one page of `POST /shadow/plausibility/backfill`. */
interface BackfillPage {
	alreadyVerified: number;
	candidates: number;
	dryRun: boolean;
	endCursor?: string;
	enqueued: number;
	failed: number;
	hasMore: boolean;
	mode: string;
	scannedEvents: number;
	/** Present on the first page only. */
	skippedEdited?: number;
	tooRecent: number;
}

const add = (totals: BackfillTotals, page: BackfillPage): BackfillTotals => ({
	alreadyVerified: totals.alreadyVerified + page.alreadyVerified,
	candidates: totals.candidates + page.candidates,
	dryRun: page.dryRun,
	enqueued: totals.enqueued + page.enqueued,
	failed: totals.failed + page.failed,
	mode: page.mode,
	scannedEvents: totals.scannedEvents + page.scannedEvents,
	skippedEdited: totals.skippedEdited + (page.skippedEdited ?? 0),
	tooRecent: totals.tooRecent + page.tooRecent,
});

/**
 * Backfill plausibility verification for the selected organization. Walks
 * every page (one page of events per request, so each stays inside API
 * Gateway's limit) and adds up the counts, reporting the running totals after
 * each page so a long run shows progress.
 */
export const useBackfill = (onProgress?: (totals: BackfillTotals) => void) =>
	useMutation({
		mutationFn: async (input: BackfillInput) => {
			let totals: BackfillTotals = {
				alreadyVerified: 0,
				candidates: 0,
				dryRun: input.dryRun,
				enqueued: 0,
				failed: 0,
				mode: "",
				scannedEvents: 0,
				skippedEdited: 0,
				tooRecent: 0,
			};
			let after: string | undefined;
			do {
				const response = await apiClient.post<BackfillPage>(
					ENDPOINTS.shadow.plausibility.backfill,
					{ ...input, after },
				);
				totals = add(totals, response.data);
				onProgress?.(totals);
				after = response.data.hasMore ? response.data.endCursor : undefined;
			} while (after);
			return totals;
		},
	});
